import { Button, IconButton, IconShape } from '@ads/components-react';
import { ExclamationCircleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import * as Dialog from '@radix-ui/react-dialog';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

export interface NotificationCancelDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isConfirming: boolean;
}

/**
 * Modal de confirmação para cancelar o envio de uma notificação agendada ou rascunho.
 *
 * @param open - Controla a visibilidade do modal
 * @param onClose - Chamado ao fechar sem confirmar
 * @param onConfirm - Chamado ao confirmar o cancelamento
 * @param isConfirming - Exibe loading no botão enquanto a requisição está em andamento
 */
export function NotificationCancelDialog({
  open,
  onClose,
  onConfirm,
  isConfirming,
}: NotificationCancelDialogProps) {
  const { t } = useTranslation('notification');

  useEffect(() => {
    if (open) return;
    const id = setTimeout(() => {
      document.body.style.removeProperty('pointer-events');
    }, 0);
    return () => clearTimeout(id);
  }, [open]);

  return (
    <Dialog.Root
      open={open}
      onOpenChange={isOpen => {
        if (!isOpen && !isConfirming) onClose();
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-none z-50 h-screen w-screen bg-base-gray-900 bg-opacity-medium" />
        <Dialog.Content
          aria-describedby="cancel-dialog-description"
          className="fixed left-1/2 top-1/2 z-50 w-full max-w-lg -translate-x-1/2 -translate-y-1/2 rounded-md bg-ctx-layout-surface p-xxs shadow-level3 focus:outline-none"
        >
          {/* Header: ícone de status + fechar */}
          <div className="mb-xs flex items-start justify-between">
            <IconShape type="danger" icon={ExclamationCircleIcon} size="md" />
            <Dialog.Close asChild>
              <IconButton
                icon={XMarkIcon}
                hierarchy="tertiary"
                size="sm"
                ariaLabel={t('cancelDialog.back')}
                disabled={isConfirming}
              />
            </Dialog.Close>
          </div>

          {/* Conteúdo */}
          <Dialog.Title className="ts-heading-xs mb-quark text-ctx-content-title">
            {t('cancelDialog.title')}
          </Dialog.Title>
          <p id="cancel-dialog-description" className="ts-paragraph-xs text-ctx-content-base">
            {t('cancelDialog.description')}
          </p>

          {/* Rodapé */}
          <div className="mt-sm flex items-center justify-end gap-xs">
            <Dialog.Close asChild>
              <Button hierarchy="tertiary" size="md" disabled={isConfirming}>
                {t('cancelDialog.back')}
              </Button>
            </Dialog.Close>
            <Button hierarchy="danger" size="md" isLoading={isConfirming} onClick={onConfirm}>
              {t('cancelDialog.confirm')}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
